/* eslint-disable nimbus/prefer-until-destroyed */
import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, ILogger, LogLevel } from '@microsoft/signalr';
import { BehaviorSubject, from, Observable, of, Subject } from 'rxjs';
import { catchError, concatAll, concatMap, delay, filter, map, takeUntil } from 'rxjs/operators';
import { LoggingService } from '../logging.service';
import { UserSettingsService } from '../user-settings.service';

@Injectable()
export abstract class BaseAssistantHub {
  private connectionIdToken: string;
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  protected signalRReconnectRetryIntervals: number[] = [100, 5_000, 5_000, 10_000, 10_000, 30_000, ...Array(59).fill(60_000)];
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  protected signalRStartRetryIntervals: number[] = [5_000, 10_000, 20_000, 30_000];
  protected startAttemptsBeforeThrowingError = 4;
  protected connection?: HubConnection;
  protected logger: ILogger;

  public isConnected$ = new BehaviorSubject<boolean>(undefined);
  public onConnected$ = new Subject<boolean>();
  public onLoggedOut$ = new Subject();

  protected abstract signalRUrl: string;

  public constructor(
    protected readonly userSettingsService: UserSettingsService,
    protected readonly loggingService: LoggingService,
  ) {
    this.logger = {} as ILogger;
    this.logger.log = (logLevel: LogLevel, message: string): void => {
      if (this.userSettingsService.userSettings?.signalRLoggingEnabled) {
        this.loggingService.Info(`SignalR [${LogLevel[logLevel]}]: ${message}`);
      }
    };
  }

  protected abstract onConnected(): void;
  protected abstract onConnectedFailed(): void;
  protected abstract subscribeEvents(): void;
  protected abstract unsubscribeEvents(): void;

  protected abstract acquireIdToken(): Observable<string>;

  protected invokeToObservable<T>(methodName: string, ...args: any[]): Observable<T> {
    return !this.connection ? of(null) : from(this.connection.invoke(methodName, ...args));
  }

  protected invokeToPromise<T>(methodName: string, ...args: any[]): Promise<T> {
    return !this.connection ? Promise.reject() : this.connection.invoke(methodName, ...args);
  }

  protected initialize(isReadyToConnect$: Observable<boolean>): void {
    this.isConnected$.pipe(filter((x) => x)).subscribe(() => {
      this.onConnected();
    });
    isReadyToConnect$.subscribe((isReadyToConnect) => {
      const connectedOrConnecting = [HubConnectionState.Connected, HubConnectionState.Connecting, HubConnectionState.Reconnecting].includes(
        this.connection?.state,
      );

      if (!isReadyToConnect) {
        this.stopConnection();
        this.onLoggedOut$.next(true);
      } else if (!connectedOrConnecting) {
        this.restartConnection();
      }
    });
  }

  private restartConnection(token: string = null): void {
    this.logger.log(LogLevel.Information, `stop and start connection`);
    this.stopConnection();
    this.startConnection(token).subscribe();
  }

  private startConnection(token: string = null): Observable<boolean> {
    const startRetryIntervals = [0, ...this.signalRStartRetryIntervals];
    let startAttempt = 0;
    return from(startRetryIntervals).pipe(
      concatMap((x) =>
        of(x).pipe(
          delay(x),
          concatMap(() =>
            (token ? of(token) : this.acquireIdToken()).pipe(
              map((idToken) => {
                this.connectionIdToken = idToken;
                const url = this.getSignalRUrl();
                this.stopConnection(); // stop connection from previous not finished start attempt
                this.connection = new HubConnectionBuilder()
                  .withUrl(url, { accessTokenFactory: () => idToken })
                  .withAutomaticReconnect([...this.signalRReconnectRetryIntervals])
                  .configureLogging(this.logger)
                  .build();
                this.subscribeEventsInternal();
                return;
              }),
            ),
          ),
          catchError((err) => {
            this.logger.log(LogLevel.Error, `Setup SignalR connection error: ${JSON.stringify(err)}`);
            return of(null);
          }),
          concatMap(async () => {
            try {
              startAttempt++;
              if (!this.connection) {
                return of(false);
              }
              await this.connection.start().catch((error) => {
                this.logger.log(LogLevel.Error, `SignalR connection start error: ${JSON.stringify(error)}`);
              });
              const connected = this.connection.state === HubConnectionState.Connected;
              this.isConnected$.next(connected);
              if (startAttempt === this.startAttemptsBeforeThrowingError) {
                this.onConnectedFailed();
              }
              if (connected || startRetryIntervals.length === startAttempt) {
                this.onConnected$.next(connected);
              }
              return of(connected);
            } catch (err) {
              if (startAttempt === 1) {
                this.logger.log(LogLevel.Error, `Start SignalR connection error: ${JSON.stringify(err)}`);
              }
              this.isConnected$.next(false);
              this.stopConnection();
              return of(false);
            }
          }),
        ),
      ),
      concatAll(),
      takeUntil(this.onConnected$),
      takeUntil(this.onLoggedOut$),
    );
  }

  private stopConnection(): void {
    this.isConnected$.next(undefined);
    if (!this.connection) {
      return;
    }
    this.unsubscribeEventsInternal();
    this.connection.stop();
    this.connection = undefined;
    this.connectionIdToken = undefined;
  }

  private subscribeEventsInternal(): void {
    if (!this.connection) {
      return;
    }
    this.unsubscribeEventsInternal();
    this.connection.onreconnecting(this.onReconnecting.bind(this));
    this.connection.onreconnected(this.onReconnected.bind(this));
    this.connection.onclose(this.onClose.bind(this));
    this.subscribeEvents();
  }

  private unsubscribeEventsInternal(): void {
    if (!this.connection) {
      return;
    }
    this.connection.onreconnecting(() => {});
    this.connection.onreconnected(() => {});
    this.connection.onclose(() => {});
    this.unsubscribeEvents();
  }

  private onReconnecting(error?: Error): void {
    this.isConnected$.next(false);
    this.logger.log(LogLevel.Information, `SignalRHub - onReconnecting`);
    if (error?.message) {
      this.logger.log(LogLevel.Information, `Reconnection error: ${error?.message}`);
    }
    this.acquireIdToken().subscribe((idToken) => {
      const notConnected = [HubConnectionState.Disconnected, HubConnectionState.Reconnecting].includes(this.connection.state);
      const tokenUpdated = this.connectionIdToken !== idToken;
      if (notConnected && tokenUpdated) {
        this.restartConnection(idToken);
      }
    });
  }

  private onReconnected(): void {
    this.isConnected$.next(true);
    this.logger.log(LogLevel.Information, 'SignalRHub - onReconnected');
  }

  private onClose(): void {
    this.isConnected$.next(false);
    this.logger.log(LogLevel.Information, 'SignalRHub - onClose');
  }

  private getSignalRUrl(): string {
    const url = new URL(`${window.location.origin}/${this.signalRUrl}`);
    return url.toString();
  }
}
